import React, { useState } from 'react';
import { useDroppable } from '@dnd-kit/core';
import {
  SortableContext,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { MoreHorizontal, Plus, Edit2, Trash2, GripVertical } from 'lucide-react';
import { Button } from '../ui/button';
import { TaskCard, TaskCardData } from './TaskCard';

export interface KanbanColumnData {
  id: string;
  name: string;
  color?: string | null;
  order: number;
  isTerminal: boolean;
}

interface KanbanColumnProps {
  column: KanbanColumnData;
  tasks: TaskCardData[];
  onAddTask: (columnId: string) => void;
  onTaskClick: (task: TaskCardData) => void;
  onEditColumn?: (column: KanbanColumnData) => void;
  onDeleteColumn?: (column: KanbanColumnData) => void;
  canConfigure: boolean;
  canCreateTasks: boolean;
  /** Whether columns can be reordered via drag (header is draggable). */
  isColumnDraggable?: boolean;
}

export const KanbanColumn: React.FC<KanbanColumnProps> = ({
  column,
  tasks,
  onAddTask,
  onTaskClick,
  onEditColumn,
  onDeleteColumn,
  canConfigure,
  canCreateTasks,
  isColumnDraggable = false,
}) => {
  const [menuOpen, setMenuOpen] = useState(false);

  // Droppable surface for empty columns / dropping at end
  const { setNodeRef: setDroppableRef, isOver } = useDroppable({
    id: `column-${column.id}`,
    data: { type: 'column', columnId: column.id },
  });

  // Sortable handle for column reorder
  const {
    attributes,
    listeners,
    setNodeRef: setSortableRef,
    transform,
    transition,
    isDragging,
  } = useSortable({
    id: `col-sort-${column.id}`,
    data: { type: 'column-sort', columnId: column.id },
    disabled: !isColumnDraggable,
  });

  const containerStyle: React.CSSProperties = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
    ...(column.color
      ? { borderLeftColor: column.color, borderLeftWidth: '4px' }
      : null),
  };

  // 8-char hex = base color + alpha; `1f` ≈ 12% opacity tint for header bg.
  const headerStyle: React.CSSProperties | undefined = column.color
    ? { backgroundColor: `${column.color}1f` }
    : undefined;

  const taskIds = tasks.map((t) => t.id);

  return (
    <div
      ref={setSortableRef}
      style={containerStyle}
      className="flex-shrink-0 w-80 bg-muted/40 dark:bg-muted/20 rounded-lg border border-border flex flex-col max-h-full"
    >
      {/* Header */}
      <div
        className="flex items-center justify-between gap-2 p-3 border-b border-border rounded-t-lg"
        style={headerStyle}
      >
        <div className="flex items-center gap-1.5 min-w-0 flex-1">
          {isColumnDraggable && (
            <button
              type="button"
              {...attributes}
              {...listeners}
              className="cursor-grab active:cursor-grabbing text-muted-foreground hover:text-foreground"
              aria-label="Przeciągnij kolumnę"
            >
              <GripVertical className="w-4 h-4" />
            </button>
          )}
          {column.color && (
            <span
              className="inline-block w-2.5 h-2.5 rounded-full flex-shrink-0 border border-black/10"
              style={{ backgroundColor: column.color }}
              aria-hidden
            />
          )}
          <h3
            className="font-semibold text-sm truncate"
            title={column.name}
          >
            {column.name}
          </h3>
          <span className="text-xs text-muted-foreground bg-background border border-border rounded-full px-2 py-0.5 ml-1">
            {tasks.length}
          </span>
        </div>

        {canConfigure && (onEditColumn || onDeleteColumn) && (
          <div className="relative">
            <Button
              type="button"
              variant="ghost"
              size="sm"
              className="h-7 w-7 p-0"
              onClick={() => setMenuOpen((v) => !v)}
            >
              <MoreHorizontal className="w-4 h-4" />
            </Button>
            {menuOpen && (
              <>
                <div
                  className="fixed inset-0 z-10"
                  onClick={() => setMenuOpen(false)}
                />
                <div className="absolute right-0 top-full mt-1 z-20 bg-popover border border-border rounded-md shadow-md min-w-[150px] py-1">
                  {onEditColumn && (
                    <button
                      type="button"
                      className="flex items-center gap-2 w-full text-left px-3 py-1.5 text-sm hover:bg-accent"
                      onClick={() => {
                        setMenuOpen(false);
                        onEditColumn(column);
                      }}
                    >
                      <Edit2 className="w-4 h-4" /> Edytuj kolumnę
                    </button>
                  )}
                  {onDeleteColumn && (
                    <button
                      type="button"
                      className="flex items-center gap-2 w-full text-left px-3 py-1.5 text-sm hover:bg-accent text-red-600"
                      onClick={() => {
                        setMenuOpen(false);
                        onDeleteColumn(column);
                      }}
                    >
                      <Trash2 className="w-4 h-4" /> Usuń kolumnę
                    </button>
                  )}
                </div>
              </>
            )}
          </div>
        )}
      </div>

      {/* Tasks list (droppable) */}
      <div
        ref={setDroppableRef}
        className={`flex-1 min-h-[100px] overflow-y-auto p-2 space-y-2 transition-colors ${
          isOver ? 'bg-primary/5 dark:bg-primary/10' : ''
        }`}
      >
        <SortableContext items={taskIds} strategy={verticalListSortingStrategy}>
          {tasks.map((task) => (
            <TaskCard key={task.id} task={task} onClick={onTaskClick} />
          ))}
        </SortableContext>

        {tasks.length === 0 && canCreateTasks && (
          <button
            type="button"
            className="w-full text-center py-8 text-sm text-muted-foreground hover:text-primary border-2 border-dashed border-border hover:border-primary/50 rounded-md transition-colors"
            onClick={() => onAddTask(column.id)}
          >
            <Plus className="w-4 h-4 inline mr-1" />
            Dodaj zadanie
          </button>
        )}
      </div>

      {/* Add task button at bottom */}
      {canCreateTasks && tasks.length > 0 && (
        <div className="p-2 border-t border-border">
          <button
            type="button"
            className="w-full flex items-center justify-center gap-1 py-1.5 text-xs text-muted-foreground hover:text-primary hover:bg-accent/30 rounded-md transition-colors"
            onClick={() => onAddTask(column.id)}
          >
            <Plus className="w-3.5 h-3.5" />
            Dodaj zadanie
          </button>
        </div>
      )}
    </div>
  );
};
